import type { InitialProfileSession, InitialProfileSessionMode, PretestResult } from './types'
import type { SupportedGrade } from '@/entities/student/types'

export type {
  InitialProfileSession,
  InitialProfileSessionMode,
  InitialProfileSessionPhase,
  InitialProfileSessionStatus,
  InitialProfileUnobservedReason,
} from './types'

export function createInitialProfileSession(input: {
  studentId: string
  grade: SupportedGrade
  mode: InitialProfileSessionMode
  now?: string
}): InitialProfileSession {
  const now = input.now ?? new Date().toISOString()
  return {
    id: `initial-profile-${input.studentId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    studentId: input.studentId,
    grade: input.grade,
    mode: input.mode,
    status: 'draft',
    phase: 'setup',
    questionIndex: 0,
    abilityAnswers: {},
    preferenceAnswers: {},
    unobservedReasons: {},
    result: null,
    startedAt: now,
    updatedAt: now,
    completedAt: null,
  }
}

export function updateInitialProfileSession(
  session: InitialProfileSession,
  patch: Partial<Pick<InitialProfileSession, 'phase' | 'questionIndex' | 'abilityAnswers' | 'preferenceAnswers' | 'unobservedReasons'>>,
  now = new Date().toISOString(),
): InitialProfileSession {
  if (session.status === 'completed') return session
  return { ...session, ...patch, updatedAt: now }
}

export function completeInitialProfileSession(
  session: InitialProfileSession,
  result: PretestResult,
  now = new Date().toISOString(),
): InitialProfileSession {
  if (session.status === 'completed') return session
  return {
    ...session,
    status: 'completed',
    phase: 'result',
    result,
    updatedAt: now,
    completedAt: now,
  }
}

export function getLatestCompletedDiagnosticSession(
  sessions: readonly InitialProfileSession[],
): InitialProfileSession | null {
  return sessions
    .filter((session) => session.mode === 'diagnostic' && session.status === 'completed' && session.result)
    .sort((left, right) => (
      new Date(right.completedAt ?? right.updatedAt).getTime()
      - new Date(left.completedAt ?? left.updatedAt).getTime()
    ))[0] ?? null
}
